import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Menu,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
  Chip,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  Alert,
  Snackbar,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton,
  Badge,
  Stack,
} from '@mui/material';
import {
  CheckCircle,
  Delete,
  Edit,
  Add,
  CompareArrows,
  CalendarToday,
  History,
  Label,
  AutoFixHigh,
  SettingsBackupRestore,
  Collections,
  Link as LinkIcon,
  Refresh,
  FilterList,
  ArrowBack,
  ArrowForward,
  ArrowDropDown,
  Help as HelpIcon,
} from '@mui/icons-material';
import axios from 'axios';
import moment from 'moment';
import { getCachedScreenshotUrl } from '../utils/screenshotCache';
import CreateGameModal from '../components/CreateGameModal';
import { useGamesCache } from '../contexts/GamesContext';

const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// ==========================================
// 🖼️ Helper component to render visual crop of promo
// ==========================================
const PromoTileCrop = ({ box, imageLocation, isSelected, onToggleSelect }) => {
  const [signedUrl, setSignedUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [aspect, setAspect] = useState(2.5); // Default aspect placeholder
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!imageLocation) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '150px' }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [imageLocation]);

  useEffect(() => {
    if (!imageLocation || !isVisible) return;
    
    let active = true;
    const fetchImage = async () => {
      try {
        setLoading(true);
        // Extract key from s3Url (e.g. s3://bucket/key)
        const key = imageLocation.replace('s3://', '').split('/').slice(1).join('/');
        const apiUrl = `${process.env.REACT_APP_API_URL}/screenshotssigned/${encodeURIComponent(key)}`;
        
        const cachedUrl = await getCachedScreenshotUrl(
          key,
          apiUrl,
          process.env.REACT_APP_API_KEY
        );

        if (active) {
          setSignedUrl(cachedUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error getting cached screenshot URL:', err);
        if (active) {
          setError(true);
          setLoading(false);
        }
      }
    };

    fetchImage();
    return () => {
      active = false;
    };
  }, [imageLocation, isVisible]);

  // Read relative values (top, left, width, height)
  const getBBValue = (boxObj, field) => {
    if (!boxObj || !boxObj.BoundingBox) return 0;
    const bb = boxObj.BoundingBox;
    // Case 1: bb is marshalled as a Map { M: { Left: { N: "0.1" } ... } }
    if (bb.M && bb.M[field]) return parseFloat(bb.M[field].N || bb.M[field].S) || 0;
    // Case 2: bb is an object with marshalled fields { Left: { N: "0.1" } ... }
    if (bb[field] && typeof bb[field] === 'object' && (bb[field].N || bb[field].S)) return parseFloat(bb[field].N || bb[field].S) || 0;
    // Case 3: bb is a plain object { Left: 0.1 ... }
    if (bb[field] !== undefined) return parseFloat(bb[field]) || 0;
    return 0;
  };

  const top = getBBValue(box, 'Top');
  const left = getBBValue(box, 'Left');
  const width = getBBValue(box, 'Width');
  const height = getBBValue(box, 'Height');

  const handleImageLoad = (e) => {
    const imgElement = e.target;
    const naturalWidth = imgElement.naturalWidth;
    const naturalHeight = imgElement.naturalHeight;
    if (naturalWidth && naturalHeight && width && height) {
      const boxPixelWidth = width * naturalWidth;
      const boxPixelHeight = height * naturalHeight;
      const calculatedAspect = boxPixelWidth / boxPixelHeight;
      // Cap aspect ratio between 0.35 and 5 to prevent extreme layout distortion
      setAspect(Math.max(0.35, Math.min(5, calculatedAspect)));
    }
  };

  const containerStyle = {
    position: 'relative',
    width: '100%',
    aspectRatio: `${aspect}`,
    overflow: 'hidden',
    border: isSelected ? '4px solid #ff9800' : '2px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#1a1a1a',
    boxShadow: isSelected ? '0 0 12px rgba(255, 152, 0, 0.6)' : '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
  };

  return (
    <Box 
      ref={containerRef}
      style={containerStyle} 
      onClick={onToggleSelect}
      sx={{
        '&:hover': {
          transform: 'scale(1.025)',
          borderColor: isSelected ? '#ff9800' : '#ffb74d',
          boxShadow: isSelected 
            ? '0 0 16px rgba(255, 152, 0, 0.8)' 
            : '0 6px 12px rgba(0,0,0,0.2)',
        }
      }}
    >
      {/* Visual Indicator of selection */}
      <Box 
        sx={{ position: 'absolute', top: 4, left: 4, zIndex: 10, bgcolor: 'rgba(0,0,0,0.6)', borderRadius: '4px', p: 0.2 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={isSelected}
          onChange={onToggleSelect}
          size="small"
          color="warning"
          sx={{ p: 0.5, color: '#fff' }}
        />
      </Box>

      {/* Date badge */}
      {box.dateLabel && (
        <Chip
          label={box.dateLabel}
          size="small"
          sx={{
            position: 'absolute',
            bottom: 4,
            right: 4,
            zIndex: 10,
            bgcolor: 'rgba(0, 0, 0, 0.75)',
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 'bold',
            height: '18px',
          }}
        />
      )}

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress size={24} color="warning" />
        </Box>
      )}

      {error && (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%" sx={{ color: 'text.secondary', p: 1, textAlign: 'center' }}>
          <Typography variant="caption">Crop unavailable</Typography>
        </Box>
      )}

      {!loading && !error && signedUrl && (
        <img
          src={signedUrl}
          alt="Captured Promotion Crop"
          onLoad={handleImageLoad}
          style={{
            position: 'absolute',
            top: `-${(top / height) * 100}%`,
            left: `-${(left / width) * 100}%`,
            width: `${(1 / width) * 100}%`,
            height: `${(1 / height) * 100}%`,
            maxWidth: 'none',
            display: 'block',
          }}
        />
      )}
    </Box>
  );
};

// ==========================================
// 🚀 Main Page Component
// ==========================================
const PromoManagement = () => {
  // Brand / Catalog variables
  const [websites, setWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [knownPromotions, setKnownPromotions] = useState([]);
  const { games, fetchGames } = useGamesCache();
  const [promotionTypes, setPromotionTypes] = useState([]);
  const [showRecentOnly, setShowRecentOnly] = useState(process.env.REACT_APP_ENV && process.env.REACT_APP_ENV.toLowerCase() !== 'dev');
  const [newPromoType, setNewPromoType] = useState('Promotion');
  const [alignPromoType, setAlignPromoType] = useState('Promotion');
  const [assignmentMode, setAssignmentMode] = useState('existing'); // 'existing' | 'new'
  const [selectedMasterPromo, setSelectedMasterPromo] = useState(null);
  const [alignPromoTextOriginal, setAlignPromoTextOriginal] = useState('');
  const [alignPromoTextTranslated, setAlignPromoTextTranslated] = useState('');
  const [isTranslatingAlign, setIsTranslatingAlign] = useState(false);

  // Filter states
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');

  const regions = useMemo(() => {
    const r = new Set(websites.map(w => w.Region).filter(Boolean));
    return ['all', ...Array.from(r).sort()];
  }, [websites]);

  const countries = useMemo(() => {
    const filtered = websites.filter(w => selectedRegion === 'all' || w.Region === selectedRegion);
    const c = new Set(filtered.map(w => w.Country).filter(Boolean));
    return ['all', ...Array.from(c).sort()];
  }, [websites, selectedRegion]);

  const tags = useMemo(() => {
    const t = new Set();
    websites.forEach(w => w.Tags?.forEach(tag => t.add(tag)));
    return ['all', ...Array.from(t).sort()];
  }, [websites]);

  const filteredWebsites = useMemo(() => {
    return websites.filter(site => {
      const matchesRegion = selectedRegion === 'all' || site.Region === selectedRegion;
      const matchesCountry = selectedCountry === 'all' || site.Country === selectedCountry;
      const matchesTag = selectedTag === 'all' || (site.Tags && site.Tags.includes(selectedTag));
      return matchesRegion && matchesCountry && matchesTag;
    });
  }, [websites, selectedRegion, selectedCountry, selectedTag]);

  // If the selected website gets filtered out, deselect it
  useEffect(() => {
    if (selectedWebsite && !filteredWebsites.some(w => w.WebsitesDBID === selectedWebsite.WebsitesDBID)) {
      setSelectedWebsite(null);
    }
  }, [filteredWebsites, selectedWebsite]);

  // What's New / How to Use Modal State
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [whatsNewLang, setWhatsNewLang] = useState('en');

  useEffect(() => {
    const seen = localStorage.getItem('whats_new_seen_promos_v1');
    if (!seen) {
      setWhatsNewOpen(true);
    }
  }, []);

  const handleCloseWhatsNew = () => {
    setWhatsNewOpen(false);
  };

  const handleDontShowWhatsNew = () => {
    localStorage.setItem('whats_new_seen_promos_v1', 'true');
    setWhatsNewOpen(false);
  };
  
  // App state
  const [startDate, setStartDate] = useState(moment.utc().startOf('week').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment.utc().startOf('week').add(6, 'days').format('YYYY-MM-DD'));

  const handleStartDateChange = (newStart) => {
    setStartDate(newStart);
    if (newStart) {
      setEndDate(moment.utc(newStart).add(6, 'days').format('YYYY-MM-DD'));
    }
  };

  const handleShiftDays = (days) => {
    setStartDate(prev => moment.utc(prev).add(days, 'days').format('YYYY-MM-DD'));
    setEndDate(prev => moment.utc(prev).add(days, 'days').format('YYYY-MM-DD'));
  };
  const [tileSize, setTileSize] = useState('medium'); // 'small' | 'medium' | 'large'
  const gridSizes = useMemo(() => {
    return {
      small: { xs: 6, sm: 4, md: 3, lg: 2 },
      medium: { xs: 12, sm: 6, md: 4, lg: 3 },
      large: { xs: 12, sm: 12, md: 8, lg: 6 }
    }[tileSize] || { xs: 12, sm: 6, md: 4, lg: 3 };
  }, [tileSize]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingScans, setLoadingScans] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  
  // Data retrieved
  const [scans, setScans] = useState([]);
  const [allPromoBoxes, setAllPromoBoxes] = useState([]);
  const [selectedBoxIds, setSelectedBoxIds] = useState({}); // { boxId: true }
  
  // UI Tabs
  const [tabValue, setTabValue] = useState(0); // 0 = Grouped by Text, 1 = Timeline, 2 = Brand Library
  
  // Dialog managers
  const [libraryDialogOpen, setLibraryDialogOpen] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [newPromoText, setNewPromoText] = useState('');
  const [newPromoGames, setNewPromoGames] = useState([]);
  const [isCreateGameModalOpen, setIsCreateGameModalOpen] = useState(false);
  const [createGameInitialName, setCreateGameInitialName] = useState('');
  const [createGameTarget, setCreateGameTarget] = useState('library'); // 'library' | 'batch'
  const [gamesInput, setGamesInput] = useState('');
  
  // Bulk align dialog
  const [alignDialogOpen, setAlignDialogOpen] = useState(false);
  const [alignPromoText, setAlignPromoText] = useState('');
  const [alignPromoGames, setAlignPromoGames] = useState([]);
  const [isAligning, setIsAligning] = useState(false);

  // Notification states
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Inline Master Promo Type Reassignment
  const [typeMenuAnchorEl, setTypeMenuAnchorEl] = useState(null);
  const [selectedClusterForType, setSelectedClusterForType] = useState(null);
  const [updatingPromoId, setUpdatingPromoId] = useState(null);
  const [addCustomTypeDialogOpen, setAddCustomTypeDialogOpen] = useState(false);
  const [customTypeName, setCustomTypeName] = useState('');

  // Combined unique promotion types for reassign dropdown
  const availablePromotionTypes = useMemo(() => {
    const defaultTypes = [
      'Promotion',
      'Sign Up Offer',
      'Retention Offer',
      'Game Promotion',
      'Loyalty Scheme',
      'Studio Promotion',
      'Other'
    ];
    const fromApi = promotionTypes.map(t => t.PromotionType).filter(Boolean);
    const combined = [...fromApi, ...defaultTypes];
    const seen = new Set();
    const result = [];
    combined.forEach(t => {
      const trimmed = t.trim();
      const lower = trimmed.toLowerCase();
      if (!seen.has(lower)) {
        seen.add(lower);
        result.push(trimmed);
      }
    });
    return result;
  }, [promotionTypes]);

  // Memoized lists to map GameID lists to objects for Autocompletes
  const alignPromoGamesObjects = useMemo(() => {
    return games.filter(g => alignPromoGames.includes(g.GameID));
  }, [games, alignPromoGames]);

  const newPromoGamesObjects = useMemo(() => {
    return games.filter(g => newPromoGames.includes(g.GameID));
  }, [games, newPromoGames]);

  // Helper to resolve GameID to Name
  const getGameName = (gameId) => {
    const gameObj = games.find(g => g.GameID === gameId);
    return gameObj ? gameObj.Name : gameId;
  };

  // Helper to assign distinct premium colors dynamically to promotion types
  const getTypeColors = (type) => {
    const normalized = (type || '').trim().toLowerCase();
    
    const presetMap = {
      'sign up offer': { bg: 'rgba(33, 150, 243, 0.08)', text: '#1565c0' }, // Blue
      'retention offer': { bg: 'rgba(76, 175, 80, 0.08)', text: '#2e7d32' }, // Green
      'game promotion': { bg: 'rgba(156, 39, 176, 0.08)', text: '#7b1fa2' }, // Purple
      'loyalty scheme': { bg: 'rgba(255, 152, 0, 0.08)', text: '#e65100' }, // Orange
      'studio promotion': { bg: 'rgba(0, 150, 136, 0.08)', text: '#00695c' }, // Teal
      'other': { bg: 'rgba(158, 158, 158, 0.08)', text: '#424242' }, // Grey
    };

    if (presetMap[normalized]) {
      return presetMap[normalized];
    }

    // Hash fallback for custom tags
    const list = [
      { bg: 'rgba(33, 150, 243, 0.08)', text: '#1565c0' },
      { bg: 'rgba(76, 175, 80, 0.08)', text: '#2e7d32' },
      { bg: 'rgba(156, 39, 176, 0.08)', text: '#7b1fa2' },
      { bg: 'rgba(255, 152, 0, 0.08)', text: '#e65100' },
      { bg: 'rgba(0, 150, 136, 0.08)', text: '#00695c' },
      { bg: 'rgba(233, 30, 99, 0.08)', text: '#c2185b' },
      { bg: 'rgba(103, 58, 183, 0.08)', text: '#4527a0' },
    ];

    let hash = 0;
    for (let i = 0; i < normalized.length; i++) {
      hash = normalized.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % list.length;
    return list[index];
  };

  // Dynamically merge master KnownPromotions library with historic promotions discovered in scans
  const mergedLibraryPromotions = useMemo(() => {
    // 1. Parse master promotions
    const masterList = knownPromotions.map(p => ({
      ...p,
      isMaster: true,
      captureCount: 0,
    }));

    // 2. Discover unique promotions in scans
    const discoveredMap = {};
    allPromoBoxes.forEach(box => {
      const descText = box.DescriptiveText?.S ?? (typeof box.DescriptiveText === 'string' ? box.DescriptiveText : '');
      const trimmed = (descText || '').trim();
      if (!trimmed) return;
      
      const key = trimmed.toLowerCase();
      
      // Get associated games
      let associated = [];
      const rawGames = box.AssociatedGames;
      if (rawGames) {
        if (rawGames.L) associated = rawGames.L.map(i => i.S || i);
        else if (Array.isArray(rawGames)) associated = rawGames;
      }

      if (!discoveredMap[key]) {
        discoveredMap[key] = {
          DescriptiveText: trimmed,
          AssociatedGames: associated,
          captureCount: 0,
          latestScanDate: box.scanDate, // The most recent scanDate since allPromoBoxes is sorted newest first
        };
      }
      discoveredMap[key].captureCount += 1;
    });

    // 3. Merge discovered into master list
    const finalList = [...masterList];
    
    Object.values(discoveredMap).forEach(disc => {
      const discKey = disc.DescriptiveText.toLowerCase();
      const existingIndex = finalList.findIndex(m => m.DescriptiveText.toLowerCase() === discKey);
      
      if (existingIndex !== -1) {
        finalList[existingIndex].captureCount = disc.captureCount;
      } else {
        finalList.push({
          id: uuidv4(),
          DescriptiveText: disc.DescriptiveText,
          AssociatedGames: disc.AssociatedGames,
          isMaster: false,
          captureCount: disc.captureCount,
          latestScanDate: disc.latestScanDate,
        });
      }
    });

    // Sort: master promotions first, then discovered by captureCount descending
    return finalList.sort((a, b) => {
      if (a.isMaster && !b.isMaster) return -1;
      if (!a.isMaster && b.isMaster) return 1;
      return b.captureCount - a.captureCount;
    });
  }, [knownPromotions, allPromoBoxes]);

  const handlePinToLibrary = async (promo) => {
    const isDuplicate = knownPromotions.some(p => 
      p.DescriptiveText.trim().toLowerCase() === promo.DescriptiveText.trim().toLowerCase()
    );
    if (isDuplicate) {
      showNotification('This promotion is already in the master library', 'info');
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/promotions`, {
        BrandID: selectedWebsite.BrandID || selectedWebsite.WebsitesDBID,
        Text: promo.DescriptiveText,
        Type: promo.Type || 'Promotion',
        AssociatedGames: promo.AssociatedGames || [],
        LastSeenAt: promo.latestScanDate || undefined
      }, {
        headers: {
          'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
          'Content-Type': 'application/json'
        }
      });
      if (response.data && response.data.Promotion) {
        const newP = response.data.Promotion;
        const mapped = {
          id: newP.PromotionID,
          DescriptiveText: newP.Text,
          OriginalText: newP.OriginalText || '',
          Type: newP.Type || 'Promotion',
          AssociatedGames: newP.AssociatedGames || [],
          CreatedAt: newP.CreatedAt,
          LastSeenAt: newP.LastSeenAt
        };
        setKnownPromotions(prev => [...prev, mapped]);
        showNotification('Promotion pinned to library successfully', 'success');
      }
    } catch (err) {
      console.error('Error pinning promotion:', err);
      showNotification('Failed to pin promotion to library', 'error');
    }
  };

  // ==========================================
  // 📥 Initial Catalog & Games Loader
  // ==========================================
  useEffect(() => {
    fetchWebsiteCatalog();
    fetchGames();
    fetchPromotionTypes();
  }, []);

  const fetchPromotionTypes = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/promotion-types`, {
        headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY },
      });
      if (response.data && response.data.promotionTypes) {
        setPromotionTypes(response.data.promotionTypes);
      }
    } catch (err) {
      console.error('Error fetching promotion types:', err);
    }
  };


  const fetchWebsiteCatalog = async () => {
    try {
      setLoadingCatalog(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/websites/catalog`, {
        method: 'GET',
        headers: { 'x-api-key': process.env.REACT_APP_API_KEY },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      const sortedWebsites = (data.websites || []).sort((a, b) => 
        a.WebsiteName.localeCompare(b.WebsiteName)
      );
      setWebsites(sortedWebsites);
    } catch (error) {
      console.error('Error fetching website catalog:', error);
      showNotification('Failed to load website list', 'error');
    } finally {
      setLoadingCatalog(false);
    }
  };

  // ==========================================
  // 📚 Known Promotions Parser Helpers
  // ==========================================
  const parseKnownPromotions = (site) => {
    if (!site || !site.KnownPromotions) return [];
    let rawPromos = [];
    if (Array.isArray(site.KnownPromotions)) {
      rawPromos = site.KnownPromotions;
    } else if (site.KnownPromotions.L) {
      rawPromos = site.KnownPromotions.L.map(item => item.S || item);
    }
    
    return rawPromos.map(p => {
      try {
        if (typeof p === 'string') {
          const parsed = JSON.parse(p);
          return {
            id: parsed.id || uuidv4(),
            DescriptiveText: parsed.DescriptiveText || '',
            AssociatedGames: parsed.AssociatedGames || [],
            rawString: p
          };
        } else {
          return {
            id: p.id || uuidv4(),
            DescriptiveText: p.DescriptiveText || '',
            AssociatedGames: p.AssociatedGames || [],
            rawString: JSON.stringify(p)
          };
        }
      } catch (e) {
        return {
          id: uuidv4(),
          DescriptiveText: String(p),
          AssociatedGames: [],
          rawString: String(p)
        };
      }
    });
  };

  const fetchBrandPromotions = async (brandId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/promotions`, {
        params: { brandId },
        headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY },
      });
      if (response.data && response.data.promotions) {
        const mapped = response.data.promotions.map(p => ({
          id: p.PromotionID,
          DescriptiveText: p.Text,
          OriginalText: p.OriginalText || '',
          Type: p.Type || 'Promotion',
          AssociatedGames: p.AssociatedGames || [],
          CreatedAt: p.CreatedAt,
          LastSeenAt: p.LastSeenAt
        }));
        setKnownPromotions(mapped);
      }
    } catch (err) {
      console.error('Error fetching brand promotions:', err);
    }
  };

  // ==========================================
  // 🔍 Promo Scan / Box Loader
  // ==========================================
  const loadBrandPromotions = async (website) => {
    if (!website) return;
    setLoadingScans(true);
    setScanProgress(5);
    setAllPromoBoxes([]);
    setSelectedBoxIds({});

    await fetchBrandPromotions(website.BrandID || website.WebsitesDBID);

    try {
      // 1. Fetch screenshot scans for the website (exactly like TimeMachine.js to guarantee GSI resolution)
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/screenshots`, {
        params: {
          websiteId: website.WebsitesDBID,
          websiteName: website.WebsiteName,
        },
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY,
        },
      });

      const fetchedScreenshots = response.data?.screenshots || [];
      
      // Filter by date range in React to guarantee maximum reliability and consistent behaviour
      const start = moment.utc(startDate).startOf('day');
      const end = moment.utc(endDate).endOf('day');
      const filteredScreenshots = fetchedScreenshots.filter(s => {
        const ts = s.Timestamp?.S || s.Timestamp;
        return ts && moment.utc(ts).isBetween(start, end);
      });

      const completeScans = filteredScreenshots.filter(s => {
        const status = (s.ScreenshotStatus?.S || s.ScreenshotStatus || '').trim().toUpperCase();
        const imageLoc = s.ImageLocation?.S || s.ImageLocation;
        return status !== 'ERROR' && imageLoc;
      });

      setScans(completeScans);
      setScanProgress(20);

      if (completeScans.length === 0) {
        setLoadingScans(false);
        setScanProgress(100);
        return;
      }

      // 2. Fetch unconfirmed analysis bounding boxes for each scan in parallel
      const totalScans = completeScans.length;
      let completedScansCount = 0;
      const loadedPromoBoxes = [];

      const fetchPromises = completeScans.map(async (scan) => {
        const scanId = scan.id?.S || scan.id;
        const scanDate = scan.Timestamp?.S || scan.Timestamp;
        const imageLocation = scan.ImageLocation?.S || scan.ImageLocation;
        const dateLabel = moment(scanDate).format('MMM D, YYYY');

        try {
          const boxResponse = await axios.get(
            `${process.env.REACT_APP_API_ANALYST_URL}/get-unconfirmed-analysis/${scanId}`,
            {
              headers: {
                'x-api-key': process.env.REACT_APP_API_ANALYST_KEY,
              },
            }
          );

          if (boxResponse.status === 200 && Array.isArray(boxResponse.data)) {
            const promoBoxes = boxResponse.data
              .filter(box => {
                const type = (box.BoxType?.S || box.BoxType || '').trim().toLowerCase();
                return type === 'promotion';
              })
              .map(box => ({
                ...box,
                scanDate,
                dateLabel,
                imageLocation,
                WebsitesDataID: scanId,
                uniqueKey: `${scanId}_${box.BoundingBoxID?.S || box.BoundingBoxID}`,
              }));

            loadedPromoBoxes.push(...promoBoxes);
          }
        } catch (err) {
          if (err.response?.status !== 404) {
            console.error(`Error loading analysis for scan ${scanId}:`, err);
          }
        } finally {
          completedScansCount += 1;
          setScanProgress(Math.min(20 + Math.round((completedScansCount / totalScans) * 80), 99));
        }
      });

      await Promise.all(fetchPromises);
      
      // Sort loaded boxes chronologically (newest first)
      loadedPromoBoxes.sort((a, b) => new Date(b.scanDate) - new Date(a.scanDate));
      setAllPromoBoxes(loadedPromoBoxes);
      setScanProgress(100);
    } catch (err) {
      console.error('Error fetching brand scans:', err);
      showNotification('Failed to retrieve captured promotions', 'error');
    } finally {
      setLoadingScans(false);
    }
  };

  // Trigger reloading when website or date range changes
  useEffect(() => {
    if (selectedWebsite) {
      loadBrandPromotions(selectedWebsite);
    }
  }, [selectedWebsite, startDate, endDate]);

  // ==========================================
  // 🧩 Text Clustering Logic
  // ==========================================
  const promoClusters = useMemo(() => {
    const groups = {};
    
    allPromoBoxes.forEach(box => {
      let key;
      let displayName;
      let isMaster = false;

      const promoID = box.PromotionID?.S || box.PromotionID;
      let masterPromo = null;
      if (promoID) {
        key = `master_${promoID}`;
        masterPromo = knownPromotions.find(p => p.id === promoID || p.PromotionID === promoID);
        displayName = masterPromo ? masterPromo.DescriptiveText : (box.DescriptiveText?.S ?? box.DescriptiveText ?? 'Unknown Master Promo');
        isMaster = true;
      } else {
        key = '__unassigned__';
        displayName = 'Unassigned Promotion Tiles';
      }

      if (!groups[key]) {
        groups[key] = {
          textKey: key,
          originalText: displayName,
          isMaster: isMaster,
          promoId: isMaster ? promoID : null,
          promoType: isMaster ? (masterPromo?.Type || 'Promotion') : null,
          masterPromo: masterPromo || null,
          associatedGames: [],
          boxes: [],
        };
      } else if (isMaster) {
        if (!groups[key].promoId) groups[key].promoId = promoID;
        if (masterPromo && (!groups[key].promoType || groups[key].promoType === 'Promotion')) {
          if (masterPromo.Type) {
            groups[key].promoType = masterPromo.Type;
            groups[key].masterPromo = masterPromo;
          }
        }
      }

      // Merge associated games to show a unique list across the cluster
      const rawGames = box.AssociatedGames;
      if (rawGames) {
        let boxGames = [];
        if (rawGames.L) boxGames = rawGames.L.map(i => i.S || i);
        else if (Array.isArray(rawGames)) boxGames = rawGames;
        
        boxGames.forEach(g => {
          if (!groups[key].associatedGames.includes(g)) {
            groups[key].associatedGames.push(g);
          }
        });
      }

      groups[key].boxes.push(box);
    });

    // Convert to sorted array
    return Object.values(groups).sort((a, b) => {
      if (a.textKey === '__unassigned__' || a.textKey === '__blank__') return 1;
      if (b.textKey === '__unassigned__' || b.textKey === '__blank__') return -1;
      // Sort Master Promos first
      if (a.isMaster && !b.isMaster) return -1;
      if (!a.isMaster && b.isMaster) return 1;
      // Then by box count
      return b.boxes.length - a.boxes.length;
    });
  }, [allPromoBoxes, knownPromotions]);

  // ==========================================
  // 📅 Timeline Grouping Logic
  // ==========================================
  const timelineScans = useMemo(() => {
    const sorted = [...scans].sort((a, b) => {
      const timeA = a.Timestamp?.S || a.Timestamp;
      const timeB = b.Timestamp?.S || b.Timestamp;
      return new Date(timeB) - new Date(timeA);
    });

    return sorted.map(scan => {
      const scanId = scan.id?.S || scan.id;
      const scanDate = scan.Timestamp?.S || scan.Timestamp;
      const imageLocation = scan.ImageLocation?.S || scan.ImageLocation;
      const boxes = allPromoBoxes.filter(b => b.WebsitesDataID === scanId);
      
      return {
        scanId,
        scanDate,
        dateLabel: moment(scanDate).format('LL'),
        timeLabel: moment(scanDate).format('LT'),
        imageLocation,
        boxes,
      };
    }).filter(s => s.boxes.length > 0);
  }, [scans, allPromoBoxes]);

  // ==========================================
  // 🧹 Recency Filtering
  // ==========================================
  const filteredPromoClusters = useMemo(() => {
    if (!showRecentOnly) return promoClusters;
    const cutoff = moment().subtract(14, 'days');
    return promoClusters.map(cluster => {
      const recentBoxes = cluster.boxes.filter(box => {
        const scanDate = box.scanDate;
        return scanDate && moment(scanDate).isAfter(cutoff);
      });
      return { ...cluster, boxes: recentBoxes };
    }).filter(cluster => cluster.boxes.length > 0);
  }, [promoClusters, showRecentOnly]);

  const filteredTimelineScans = useMemo(() => {
    if (!showRecentOnly) return timelineScans;
    const cutoff = moment().subtract(14, 'days');
    return timelineScans.filter(scan => {
      return scan.scanDate && moment(scan.scanDate).isAfter(cutoff);
    });
  }, [timelineScans, showRecentOnly]);

  const filteredLibraryPromotions = useMemo(() => {
    if (!showRecentOnly) return mergedLibraryPromotions;
    const cutoff = moment().subtract(14, 'days');
    return mergedLibraryPromotions.filter(promo => {
      const lastSeen = promo.LastSeenAt;
      const isRecentLastSeen = lastSeen && moment(lastSeen).isAfter(cutoff);
      return promo.captureCount > 0 || isRecentLastSeen;
    });
  }, [mergedLibraryPromotions, showRecentOnly]);

  // ==========================================
  // 🤝 Checkbox Managers
  // ==========================================
  const handleToggleSelectBox = (uniqueKey) => {
    setSelectedBoxIds(prev => ({
      ...prev,
      [uniqueKey]: !prev[uniqueKey]
    }));
  };

  const handleSelectAllInCluster = (cluster, value) => {
    const updates = {};
    cluster.boxes.forEach(box => {
      updates[box.uniqueKey] = value;
    });
    setSelectedBoxIds(prev => ({ ...prev, ...updates }));
  };

  const handleSelectAllInScan = (scan, value) => {
    const updates = {};
    scan.boxes.forEach(box => {
      updates[box.uniqueKey] = value;
    });
    setSelectedBoxIds(prev => ({ ...prev, ...updates }));
  };

  const selectedBoxesList = useMemo(() => {
    return allPromoBoxes.filter(box => selectedBoxIds[box.uniqueKey]);
  }, [allPromoBoxes, selectedBoxIds]);

  const clearSelection = () => {
    setSelectedBoxIds({});
  };

  // ==========================================
  // 🪄 Bulk Alignment Wizard
  // ==========================================
  const openAlignWizard = async () => {
    if (selectedBoxesList.length === 0) return;
    
    // Pick the most common non-empty text among selected boxes as default suggestion
    const textCounts = {};
    let bestText = '';
    let maxCount = 0;
    let bestGames = [];

    selectedBoxesList.forEach(box => {
      const text = box.RecognizedText?.S || box.RecognizedText || box.RecognisedText?.S || box.RecognisedText || box.DescriptiveText?.S || (typeof box.DescriptiveText === 'string' ? box.DescriptiveText : '');
      if (!text) return;
      textCounts[text] = (textCounts[text] || 0) + 1;
      
      if (textCounts[text] > maxCount) {
        maxCount = textCounts[text];
        bestText = text;
        
        let games = [];
        const rawGames = box.AssociatedGames;
        if (rawGames) {
          if (rawGames.L) games = rawGames.L.map(i => i.S || i);
          else if (Array.isArray(rawGames)) games = rawGames;
        }
        bestGames = games;
      }
    });

    // If no text was found, check known library
    if (!bestText && knownPromotions.length > 0) {
      bestText = knownPromotions[0].DescriptiveText;
      bestGames = knownPromotions[0].AssociatedGames;
    }

    setAlignPromoTextOriginal(bestText);
    setAlignPromoTextTranslated('');
    setAlignPromoText('');
    setAlignPromoGames(bestGames);
    setAlignPromoType('Promotion');
    setSelectedMasterPromo(null);
    setAssignmentMode(knownPromotions.length > 0 ? 'existing' : 'new');
    setAlignDialogOpen(true);

    if (bestText) {
      setIsTranslatingAlign(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/translate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY
          },
          body: JSON.stringify({
            text: bestText,
            target_language: 'en'
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          const translated = data.translated_text || '';
          
          if (translated) {
            setAlignPromoTextTranslated(translated);
            
            // Now attempt match on both original and translated text
            const originalQuery = bestText.toLowerCase().trim();
            const translatedQuery = translated.toLowerCase().trim();
            const matchedPromo = knownPromotions.find(p => 
              (p.DescriptiveText || '').toLowerCase().trim() === originalQuery ||
              (p.OriginalText || '').toLowerCase().trim() === originalQuery ||
              (p.DescriptiveText || '').toLowerCase().trim() === translatedQuery ||
              (p.OriginalText || '').toLowerCase().trim() === translatedQuery
            );

            if (matchedPromo) {
              setSelectedMasterPromo(matchedPromo);
              setAssignmentMode('existing');
              setAlignPromoText(matchedPromo.DescriptiveText);
              setAlignPromoGames(matchedPromo.AssociatedGames || []);
              setAlignPromoType(matchedPromo.Type || 'Promotion');
            }
          }
        }
      } catch (err) {
        console.error('Error translating batch promo text:', err);
      } finally {
        setIsTranslatingAlign(false);
      }
    }
  };

  const executeBulkAlignment = async () => {
    let promotionId = null;
    let targetGames = [];

    if (assignmentMode === 'existing') {
      if (!selectedMasterPromo) {
        showNotification('Please select a Master Promotion to assign', 'warning');
        return;
      }
      promotionId = selectedMasterPromo.id;
      targetGames = selectedMasterPromo.AssociatedGames || [];
    } else {
      if (!alignPromoText.trim()) {
        showNotification('Master Promo Name is required', 'warning');
        return;
      }
      targetGames = alignPromoGames;
    }

    setIsAligning(true);
    const targetBoxes = [...selectedBoxesList];
    let successCount = 0;

    try {
      // Find the most recent scanDate among the target boxes
      let latestScanDateStr = null;
      targetBoxes.forEach(box => {
        if (box.scanDate) {
          if (!latestScanDateStr || new Date(box.scanDate) > new Date(latestScanDateStr)) {
            latestScanDateStr = box.scanDate;
          }
        }
      });

      // 1. Create Promotion in catalog if 'new' mode
      if (assignmentMode === 'new') {
        const isDuplicate = knownPromotions.some(p => 
          p.DescriptiveText.trim().toLowerCase() === alignPromoText.trim().toLowerCase()
        );
        if (isDuplicate) {
          showNotification('A promotion with this name already exists in the library', 'warning');
          setIsAligning(false);
          return;
        }

        try {
          const response = await axios.post(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/promotions`, {
            BrandID: selectedWebsite.BrandID || selectedWebsite.WebsitesDBID,
            Text: alignPromoText.trim(),
            Type: alignPromoType || 'Promotion',
            AssociatedGames: alignPromoGames,
            LastSeenAt: latestScanDateStr || undefined,
            OriginalText: alignPromoTextOriginal || undefined,
            AutoTranslatedText: alignPromoTextTranslated || undefined
          }, {
            headers: {
              'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
              'Content-Type': 'application/json'
            }
          });
          if (response.data && response.data.Promotion) {
            const newP = response.data.Promotion;
            promotionId = newP.PromotionID;
            setKnownPromotions(prev => [...prev, {
              id: newP.PromotionID,
              DescriptiveText: newP.Text,
              OriginalText: newP.OriginalText || '',
              AutoTranslatedText: newP.AutoTranslatedText || '',
              Type: newP.Type || 'Promotion',
              AssociatedGames: newP.AssociatedGames || [],
              CreatedAt: newP.CreatedAt,
              LastSeenAt: newP.LastSeenAt
            }]);
            
            // Save global promotion type if new
            if (alignPromoType && !promotionTypes.some(t => t.PromotionType.toLowerCase() === alignPromoType.toLowerCase())) {
              axios.post(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/promotion-types`, {
                PromotionType: alignPromoType,
                Description: 'Automatically created from Bulk Assignment'
              }, {
                headers: {
                  'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
                  'Content-Type': 'application/json'
                }
              }).then(res => {
                if (res.data && res.data.PromotionType) {
                  setPromotionTypes(prev => [...prev, res.data.PromotionType]);
                }
              }).catch(err => console.error("Failed to create promotion type globally", err));
            }
          }
        } catch (createErr) {
          console.error("Error creating aligned promotion:", createErr);
          showNotification('Failed to create new Master Promo', 'error');
          setIsAligning(false);
          return;
        }
      } else {
        // If assigning to existing, check if we need to update LastSeenAt on the library promo
        if (latestScanDateStr && selectedMasterPromo) {
          const scanDateObj = new Date(latestScanDateStr);
          const lastSeenObj = selectedMasterPromo.LastSeenAt ? new Date(selectedMasterPromo.LastSeenAt) : new Date(0);
          if (scanDateObj > lastSeenObj) {
            try {
              const updatedLastSeen = scanDateObj.toISOString();
              await axios.put(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/promotions/${selectedMasterPromo.id}`, {
                LastSeenAt: updatedLastSeen
              }, {
                headers: {
                  'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
                  'Content-Type': 'application/json'
                }
              });
              
              setKnownPromotions(prev => prev.map(p => 
                p.id === selectedMasterPromo.id 
                  ? { ...p, LastSeenAt: updatedLastSeen } 
                  : p
              ));
            } catch (err) {
              console.error("❌ Failed to update LastSeenAt on master promotion:", err);
            }
          }
        }
      }

      // 2. Process API calls to update each selected bounding box
      const updatePromises = targetBoxes.map(async (box) => {
        const websitesDataId = box.WebsitesDataID;
        const bId = box.BoundingBoxID?.S || box.BoundingBoxID;

        const requestBody = {
          WebsitesDataID: websitesDataId,
          BoundingBoxID: bId,
          BoxType: 'Promotion',
          PromotionID: promotionId || undefined,
          AssociatedGames: targetGames,
          Status: 'COMPLETE',
          stage: 'ManualMatch',
          RemoveAttributes: [
            'DescriptiveText', 'Tag', 'GameID', 'AnalysedText', 'DetectedText', 'LLMResponse', 
            'RecognisedText', 'RecognizedText', 'ConfirmedMatch',
            'RowPosition', 'ColumnPosition', 'FeaturedRank',
            'SizeCategory', 'NearestRowNumber', 'DistanceToNearestRow'
          ],
          TriggerPositionAnalysis: true
        };

        try {
          await axios.post(`${process.env.REACT_APP_API_ANALYST_URL}/set-unconfirmed-analysis`, requestBody, {
            headers: {
              'x-api-key': process.env.REACT_APP_API_ANALYST_KEY,
              'Content-Type': 'application/json',
            },
          });
          successCount += 1;
        } catch (err) {
          console.error(`Error assigning bounding box ${bId} on scan ${websitesDataId}:`, err);
        }
      });

      await Promise.all(updatePromises);

      showNotification(`Successfully assigned ${successCount} of ${targetBoxes.length} promotion tiles`, 'success');
      
      // Reload box data
      loadBrandPromotions(selectedWebsite);
      setAlignDialogOpen(false);
      clearSelection();
    } catch (err) {
      console.error('Error during bulk assignment operation:', err);
      showNotification('Failed to complete assignment batch', 'error');
    } finally {
      setIsAligning(false);
    }
  };

  // ==========================================
  // 📚 Brand Library Managers (CRUDS)
  // ==========================================
  const openLibraryAdd = () => {
    setEditingPromotion(null);
    setNewPromoText('');
    setNewPromoType('Promotion');
    setNewPromoGames([]);
    setGamesInput('');
    setLibraryDialogOpen(true);
  };

  const openLibraryEdit = (promo) => {
    setEditingPromotion(promo);
    setNewPromoText(promo.DescriptiveText);
    setNewPromoType(promo.Type || 'Promotion');
    setNewPromoGames(promo.AssociatedGames || []);
    setGamesInput('');
    setLibraryDialogOpen(true);
  };

  const handleSaveLibraryPromo = async () => {
    if (!newPromoText.trim()) {
      showNotification('Promotion text cannot be empty', 'warning');
      return;
    }

    try {
      if (editingPromotion) {
        // Edit mode
        const response = await axios.put(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/promotions/${editingPromotion.id}`, {
          Text: newPromoText.trim(),
          Type: newPromoType || 'Promotion',
          AssociatedGames: newPromoGames
        }, {
          headers: {
            'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
            'Content-Type': 'application/json'
          }
        });
        if (response.status === 200) {
          const updated = response.data.Promotion;
          setKnownPromotions(prev => prev.map(p => 
            p.id === editingPromotion.id 
              ? {
                  id: updated.PromotionID,
                  DescriptiveText: updated.Text,
                  OriginalText: updated.OriginalText || '',
                  Type: updated.Type || 'Promotion',
                  AssociatedGames: updated.AssociatedGames || [],
                  CreatedAt: updated.CreatedAt,
                  LastSeenAt: updated.LastSeenAt
                }
              : p
          ));
          showNotification('Promotion updated successfully', 'success');
          
          // Save type tag globally if new
          if (newPromoType && !promotionTypes.some(t => t.PromotionType.toLowerCase() === newPromoType.toLowerCase())) {
            axios.post(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/promotion-types`, {
              PromotionType: newPromoType,
              Description: 'Automatically created from Promo Manager'
            }, {
              headers: {
                'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
                'Content-Type': 'application/json'
              }
            }).then(res => {
              if (res.data && res.data.PromotionType) {
                setPromotionTypes(prev => [...prev, res.data.PromotionType]);
              }
            }).catch(err => console.error("Failed to create promotion type globally", err));
          }
        }
      } else {
        // Add mode
        const isDuplicate = knownPromotions.some(p => 
          p.DescriptiveText.trim().toLowerCase() === newPromoText.trim().toLowerCase()
        );
        if (isDuplicate) {
          showNotification('This promotion text already exists in the library', 'warning');
          return;
        }

        const response = await axios.post(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/promotions`, {
          BrandID: selectedWebsite.BrandID || selectedWebsite.WebsitesDBID,
          Text: newPromoText.trim(),
          Type: newPromoType || 'Promotion',
          AssociatedGames: newPromoGames
        }, {
          headers: {
            'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
            'Content-Type': 'application/json'
          }
        });
        if (response.data && response.data.Promotion) {
          const newP = response.data.Promotion;
          const mapped = {
            id: newP.PromotionID,
            DescriptiveText: newP.Text,
            OriginalText: newP.OriginalText || '',
            Type: newP.Type || 'Promotion',
            AssociatedGames: newP.AssociatedGames || [],
            CreatedAt: newP.CreatedAt,
            LastSeenAt: newP.LastSeenAt
          };
          setKnownPromotions(prev => [...prev, mapped]);
          showNotification('Promotion created successfully', 'success');
          
          // Save type tag globally if new
          if (newPromoType && !promotionTypes.some(t => t.PromotionType.toLowerCase() === newPromoType.toLowerCase())) {
            axios.post(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/promotion-types`, {
              PromotionType: newPromoType,
              Description: 'Automatically created from Promo Manager'
            }, {
              headers: {
                'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
                'Content-Type': 'application/json'
              }
            }).then(res => {
              if (res.data && res.data.PromotionType) {
                setPromotionTypes(prev => [...prev, res.data.PromotionType]);
              }
            }).catch(err => console.error("Failed to create promotion type globally", err));
          }
        }
      }
      setLibraryDialogOpen(false);
    } catch (err) {
      console.error('Error saving library promotion:', err);
      showNotification('Failed to save promotion to database', 'error');
    }
  };

  const handleDeleteLibraryPromo = async (promoId) => {
    if (window.confirm('Are you sure you want to delete this promotion? This will not alter past aligned tiles, but will remove it from the catalog.')) {
      try {
        const response = await axios.delete(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/promotions/${promoId}`, {
          headers: {
            'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY
          }
        });
        if (response.status === 200) {
          setKnownPromotions(prev => prev.filter(p => p.id !== promoId));
          showNotification('Promotion deleted successfully', 'success');
        }
      } catch (err) {
        console.error('Error deleting promotion:', err);
        showNotification('Failed to delete promotion from database', 'error');
      }
    }
  };

  // ==========================================
  // 🏷️ Inline Promo Type Reassignment Handlers
  // ==========================================
  const handleOpenTypeMenu = (event, cluster) => {
    event.stopPropagation();
    setTypeMenuAnchorEl(event.currentTarget);
    setSelectedClusterForType(cluster);
  };

  const handleCloseTypeMenu = () => {
    setTypeMenuAnchorEl(null);
  };

  const handleSelectType = async (type) => {
    const cluster = selectedClusterForType;
    handleCloseTypeMenu();
    if (!cluster) return;
    await handleReassignPromoType(cluster, type);
  };

  const handleOpenCustomTypeDialog = () => {
    handleCloseTypeMenu();
    setCustomTypeName('');
    setAddCustomTypeDialogOpen(true);
  };

  const handleConfirmCustomType = async () => {
    if (!customTypeName.trim() || !selectedClusterForType) return;
    const newType = customTypeName.trim();
    setAddCustomTypeDialogOpen(false);
    await handleReassignPromoType(selectedClusterForType, newType);
  };

  const handleReassignPromoType = async (cluster, newType) => {
    if (!cluster?.promoId || !newType) return;
    const cleanType = newType.trim();
    if (cleanType === (cluster.promoType || 'Promotion')) return;

    setUpdatingPromoId(cluster.promoId);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/promotions/${cluster.promoId}`,
        {
          Type: cleanType
        },
        {
          headers: {
            'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        const updated = response.data?.Promotion;
        const finalType = updated?.Type || cleanType;

        // Update knownPromotions state immediately
        setKnownPromotions(prev => {
          const exists = prev.some(p => p.id === cluster.promoId || p.PromotionID === cluster.promoId);
          if (exists) {
            return prev.map(p => 
              (p.id === cluster.promoId || p.PromotionID === cluster.promoId)
                ? { ...p, Type: finalType }
                : p
            );
          } else {
            return [...prev, {
              id: cluster.promoId,
              PromotionID: cluster.promoId,
              DescriptiveText: cluster.originalText,
              Type: finalType,
              AssociatedGames: cluster.associatedGames || []
            }];
          }
        });

        showNotification(`Promo type updated to "${finalType}"`, 'success');

        // Save new type tag globally if not present
        if (!promotionTypes.some(t => (t.PromotionType || '').toLowerCase() === finalType.toLowerCase())) {
          axios.post(`${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/promotion-types`, {
            PromotionType: finalType,
            Description: 'Automatically created from Promo Manager'
          }, {
            headers: {
              'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY,
              'Content-Type': 'application/json'
            }
          }).then(res => {
            if (res.data && res.data.PromotionType) {
              setPromotionTypes(prev => [...prev, res.data.PromotionType]);
            }
          }).catch(err => console.error("Failed to create promotion type globally", err));
        }
      }
    } catch (err) {
      console.error('Error reassigning promo type:', err);
      showNotification('Failed to update promo type', 'error');
    } finally {
      setUpdatingPromoId(null);
    }
  };

  // Games parsing inside dialogs
  const handleAddGameChip = () => {
    if (gamesInput.trim() && !newPromoGames.includes(gamesInput.trim())) {
      setNewPromoGames(prev => [...prev, gamesInput.trim()]);
      setGamesInput('');
    }
  };

  const handleRemoveGameChip = (gameToRemove) => {
    setNewPromoGames(prev => prev.filter(g => g !== gameToRemove));
  };

  const handleAddAlignGameChip = (gameInputText) => {
    const trimmed = gameInputText.trim();
    if (trimmed && !alignPromoGames.includes(trimmed)) {
      setAlignPromoGames(prev => [...prev, trimmed]);
    }
  };

  const handleRemoveAlignGameChip = (gameToRemove) => {
    setAlignPromoGames(prev => prev.filter(g => g !== gameToRemove));
  };

  // ==========================================
  // 🔔 Utility Notifications
  // ==========================================
  const showNotification = (msg, severity = 'success') => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  return (
    <Box sx={{ py: 4, px: 1, pb: 15, maxWidth: '1400px', margin: '0 auto' }}>
      
      {/* 🚀 Header Banner */}
      <Box 
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #1e1e1e 0%, #303030 100%)',
          color: '#fff',
          boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 800, 
              background: 'linear-gradient(45deg, #ff9800 30%, #ff5722 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.5px',
            }}
          >
            Promo Tiles Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#b0bec5', mt: 0.5, fontWeight: 500 }}>
            Ensure descriptive text alignment, eliminate typos, and manage historical promotion assets visually.
          </Typography>
        </Box>

        <Box display="flex" gap={2.5} alignItems="center" flexWrap="wrap">
          {/* Date Picker Range controls */}
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton 
              onClick={() => handleShiftDays(-7)}
              sx={{ color: '#ff9800', bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
              size="small"
            >
              <ArrowBack />
            </IconButton>
            <TextField
              type="date"
              label="Start Date (UTC)"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                width: '160px',
                bgcolor: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  height: '38px',
                  fontSize: '0.875rem',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.15)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#ff9800',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#ff9800',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.75rem',
                  transform: 'translate(14px, -8px) scale(0.75)',
                  '&.Mui-focused': {
                    color: '#ff9800',
                  }
                },
                '& input::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1)',
                }
              }}
            />
            <TextField
              type="date"
              label="End Date (UTC)"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate }}
              sx={{
                width: '160px',
                bgcolor: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  height: '38px',
                  fontSize: '0.875rem',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.15)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#ff9800',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#ff9800',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.75rem',
                  transform: 'translate(14px, -8px) scale(0.75)',
                  '&.Mui-focused': {
                    color: '#ff9800',
                  }
                },
                '& input::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1)',
                }
              }}
            />
            <IconButton 
              onClick={() => handleShiftDays(7)}
              sx={{ color: '#ff9800', bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
              size="small"
            >
              <ArrowForward />
            </IconButton>
          </Box>

          <Box sx={{ color: '#b0bec5', fontSize: '0.85rem', fontWeight: 600, minWidth: '150px' }}>
            Range: {moment.utc(startDate).format('MMM D')} – {moment.utc(endDate).format('MMM D, YYYY')}
          </Box>

          {/* Tile Size selection */}
          <ToggleButtonGroup
            value={tileSize}
            exclusive
            onChange={(e, val) => val && setTileSize(val)}
            size="small"
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.1)',
              height: '38px',
              '& .MuiToggleButton-root': {
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.1)',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                px: 2,
                '&.Mui-selected': {
                  bgcolor: '#ff9800',
                  color: '#fff',
                  '&:hover': {
                    bgcolor: '#f57c00',
                  }
                }
              }
            }}
          >
            <ToggleButton value="small">Small Tiles</ToggleButton>
            <ToggleButton value="medium">Medium</ToggleButton>
            <ToggleButton value="large">Large Tiles</ToggleButton>
          </ToggleButtonGroup>

          {selectedWebsite && (
            <Tooltip title="Reload captured promotions">
              <IconButton 
                onClick={() => loadBrandPromotions(selectedWebsite)} 
                disabled={loadingScans}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.05)', 
                  color: '#ff9800', 
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } 
                }}
              >
                <Refresh className={loadingScans ? 'spin-animation' : ''} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* 🔍 Autocomplete Selection */}
      <Card 
        sx={{ 
          mb: 4, 
          borderRadius: '12px', 
          border: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            {/* Filter Section */}
            {!loadingCatalog && (
              <>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    options={regions}
                    size="small"
                    value={selectedRegion}
                    onChange={(event, val) => {
                      setSelectedRegion(val || 'all');
                      setSelectedCountry('all');
                    }}
                    renderInput={(params) => <TextField {...params} label="Region" variant="outlined" />}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    options={countries}
                    size="small"
                    value={selectedCountry}
                    onChange={(event, val) => setSelectedCountry(val || 'all')}
                    renderInput={(params) => <TextField {...params} label="Country" variant="outlined" />}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    options={tags}
                    size="small"
                    value={selectedTag}
                    onChange={(event, val) => setSelectedTag(val || 'all')}
                    renderInput={(params) => <TextField {...params} label="Tag" variant="outlined" />}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={8}>
              {loadingCatalog ? (
                <Box display="flex" alignItems="center" gap={2}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading brands and operators...</Typography>
                </Box>
              ) : (
                <Autocomplete
                  options={filteredWebsites}
                  getOptionLabel={(option) => `${option.WebsiteName} (${option.Country || 'BR'})`}
                  value={selectedWebsite}
                  onChange={(event, newValue) => {
                    setSelectedWebsite(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Select Operator Website / Brand" 
                      placeholder="Type brand name..."
                      variant="outlined" 
                      fullWidth
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {selectedWebsite ? (
                <Box sx={{ pl: { md: 2 } }}>
                  <Typography variant="caption" color="text.secondary" display="block">Selected operator ID:</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{selectedWebsite.WebsitesDBID}</Typography>
                </Box>
              ) : (
                <Alert severity="info" sx={{ py: 0.5 }}>Please select a website to retrieve captured promotions.</Alert>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 🔄 Scan Loading bar */}
      {loadingScans && (
        <Box sx={{ width: '100%', mb: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
            Analyzing screenshots and extracting promotion tiles ({scanProgress}%)
          </Typography>
          <LinearProgress variant="determinate" value={scanProgress} color="warning" sx={{ height: '8px', borderRadius: '4px' }} />
        </Box>
      )}

      {selectedWebsite && !loadingScans && (
        <>
          {/* 🗂️ View Selectors (Tabs) */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={tabValue} 
              onChange={(e, val) => setTabValue(val)}
              textColor="warning"
              indicatorColor="warning"
              aria-label="Promo management tabs"
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  fontSize: '0.95rem',
                }
              }}
            >
              <Tab 
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <CompareArrows size={18} />
                    Grouped Similarity
                    <Badge badgeContent={filteredPromoClusters.length} color="warning" sx={{ ml: 1, '& .MuiBadge-badge': { top: -2 } }} />
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarToday size={18} />
                    Scans Timeline
                    <Badge badgeContent={filteredTimelineScans.length} color="secondary" sx={{ ml: 1, '& .MuiBadge-badge': { top: -2 } }} />
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <History size={18} />
                    Brand Library
                    <Badge badgeContent={filteredLibraryPromotions.length} color="info" sx={{ ml: 1, '& .MuiBadge-badge': { top: -2 } }} />
                  </Box>
                } 
              />
            </Tabs>
          </Box>

          {/* ========================================== */}
          {/* TAB 0: Grouped Similarity                  */}
          {/* ========================================== */}
          {tabValue === 0 && (
            <Box>
              {filteredPromoClusters.length === 0 ? (
                <Box textAlign="center" py={8}>
                  <Collections sx={{ fontSize: '48px', color: 'text.secondary', opacity: 0.5, mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">No promotion tiles detected</Typography>
                  <Typography variant="body2" color="text.secondary">
                    No boxes marked as "Promotion" were found in screenshots for the selected date range.
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {filteredPromoClusters.map((cluster) => {
                    const isBlank = cluster.textKey === '__blank__';
                    const hasSelections = cluster.boxes.some(b => selectedBoxIds[b.uniqueKey]);
                    const allSelected = cluster.boxes.every(b => selectedBoxIds[b.uniqueKey]);

                    return (
                      <Grid item xs={12} key={cluster.textKey}>
                        <Card 
                          sx={{ 
                            borderRadius: '12px',
                            borderLeft: isBlank ? '6px solid #e0e0e0' : '6px solid #ff9800',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.06)',
                          }}
                        >
                          {/* Cluster Header */}
                          <Box 
                            sx={{ 
                              p: 2, 
                              bgcolor: 'rgba(0,0,0,0.02)', 
                              borderBottom: '1px solid rgba(0,0,0,0.06)',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              flexWrap: 'wrap',
                              gap: 2,
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <Typography 
                                  variant="subtitle1" 
                                  sx={{ 
                                    fontWeight: 700, 
                                    color: isBlank ? 'text.secondary' : 'text.primary',
                                    fontStyle: isBlank ? 'italic' : 'normal',
                                  }}
                                >
                                  {cluster.originalText}
                                </Typography>
                                {cluster.isMaster && (
                                  <Chip 
                                    label="Master Promo" 
                                    size="small" 
                                    sx={{ 
                                      bgcolor: 'rgba(255, 152, 0, 0.1)', 
                                      color: '#ff9800', 
                                      fontWeight: 'bold',
                                      border: '1px solid rgba(255, 152, 0, 0.3)' 
                                    }} 
                                  />
                                )}
                                <Chip 
                                  label={`${cluster.boxes.length} tile${cluster.boxes.length > 1 ? 's' : ''}`}
                                  size="small" 
                                  color={isBlank ? "default" : "warning"}
                                  sx={{ fontWeight: 'bold' }}
                                />
                                {cluster.isMaster && (() => {
                                  const currentType = cluster.promoType || 'Promotion';
                                  const colors = getTypeColors(currentType);
                                  const isUpdating = updatingPromoId === cluster.promoId;

                                  return (
                                    <Tooltip title={`Master Promo Type: ${currentType} (Click to reassign)`} arrow>
                                      <Chip 
                                        icon={<Label sx={{ fontSize: '13px !important', color: `${colors.text} !important`, ml: '4px' }} />}
                                        label={
                                          <Box display="inline-flex" alignItems="center" gap={0.5}>
                                            <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 700, color: colors.text }}>
                                              {currentType}
                                            </Typography>
                                            {isUpdating ? (
                                              <CircularProgress size={12} sx={{ color: colors.text, ml: 0.25 }} />
                                            ) : (
                                              <ArrowDropDown sx={{ fontSize: '16px !important', color: colors.text, ml: -0.25 }} />
                                            )}
                                          </Box>
                                        }
                                        size="small" 
                                        onClick={(e) => !isUpdating && handleOpenTypeMenu(e, cluster)}
                                        clickable={!isUpdating}
                                        sx={{ 
                                          bgcolor: colors.bg, 
                                          color: colors.text, 
                                          border: `1px solid ${colors.text}40`,
                                          height: '24px',
                                          cursor: isUpdating ? 'wait' : 'pointer',
                                          transition: 'all 0.15s ease-in-out',
                                          '&:hover': {
                                            bgcolor: colors.bg,
                                            border: `1px solid ${colors.text}`,
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                                            transform: 'translateY(-1px)',
                                          },
                                          '&:active': {
                                            transform: 'translateY(0)',
                                          }
                                        }} 
                                      />
                                    </Tooltip>
                                  );
                                })()}
                              </Box>

                              {/* Associated Games */}
                              {!isBlank && cluster.associatedGames.length > 0 && (
                                <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
                                  <Typography variant="caption" sx={{ alignSelf: 'center', mr: 1, fontWeight: 600 }}>
                                    Games:
                                  </Typography>
                                  {cluster.associatedGames.map((game, i) => (
                                    <Chip key={i} label={getGameName(game)} size="small" variant="outlined" sx={{ height: '20px', fontSize: '0.75rem' }} />
                                  ))}
                                </Box>
                              )}
                            </Box>

                            <Box>
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                onClick={() => handleSelectAllInCluster(cluster, !allSelected)}
                                sx={{ mr: 1 }}
                              >
                                {allSelected ? 'Deselect All' : 'Select All'}
                              </Button>
                            </Box>
                          </Box>

                          {/* Cluster Tiles Grid */}
                          <CardContent sx={{ p: 3 }}>
                            <Grid container spacing={2}>
                              {cluster.boxes.map((box) => (
                                <Grid item {...gridSizes} key={box.uniqueKey}>
                                  <PromoTileCrop
                                    box={box}
                                    imageLocation={box.imageLocation}
                                    isSelected={!!selectedBoxIds[box.uniqueKey]}
                                    onToggleSelect={() => handleToggleSelectBox(box.uniqueKey)}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          )}

          {/* ========================================== */}
          {/* TAB 1: Timeline Scans                      */}
          {/* ========================================== */}
          {tabValue === 1 && (
            <Box>
              {filteredTimelineScans.length === 0 ? (
                <Box textAlign="center" py={8}>
                  <CalendarToday sx={{ fontSize: '48px', color: 'text.secondary', opacity: 0.5, mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">No chronological scans available</Typography>
                  <Typography variant="body2" color="text.secondary">
                    We couldn't retrieve screenshot records containing promotions.
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={4}>
                  {filteredTimelineScans.map((scan) => {
                    const allSelected = scan.boxes.every(b => selectedBoxIds[b.uniqueKey]);
                    
                    return (
                      <Grid item xs={12} key={scan.scanId}>
                        <Card 
                          sx={{ 
                            borderRadius: '12px',
                            border: '1px solid rgba(0,0,0,0.08)',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.04)',
                          }}
                        >
                          <Box 
                            sx={{ 
                              p: 2, 
                              bgcolor: 'rgba(255, 152, 0, 0.05)',
                              borderBottom: '1px solid rgba(0,0,0,0.06)',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <CalendarToday sx={{ color: '#ff9800' }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {scan.dateLabel}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ({scan.timeLabel})
                              </Typography>
                              <Chip 
                                label={`${scan.boxes.length} captured tile${scan.boxes.length > 1 ? 's' : ''}`}
                                size="small" 
                                color="warning"
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>

                            <Box>
                              <Button
                                size="small"
                                variant="text"
                                color="warning"
                                onClick={() => handleSelectAllInScan(scan, !allSelected)}
                              >
                                {allSelected ? 'Deselect Scan' : 'Select Scan'}
                              </Button>
                            </Box>
                          </Box>

                          <CardContent sx={{ p: 3 }}>
                            <Grid container spacing={2}>
                              {scan.boxes.map((box) => {
                                const desc = box.DescriptiveText?.S ?? (typeof box.DescriptiveText === 'string' ? box.DescriptiveText : '');
                                return (
                                  <Grid item {...gridSizes} key={box.uniqueKey}>
                                    <Box display="flex" flexDirection="column" gap={1}>
                                      <PromoTileCrop
                                        box={box}
                                        imageLocation={box.imageLocation}
                                        isSelected={!!selectedBoxIds[box.uniqueKey]}
                                        onToggleSelect={() => handleToggleSelectBox(box.uniqueKey)}
                                      />
                                      <Box sx={{ px: 0.5 }}>
                                        <Typography 
                                          variant="caption" 
                                          display="block" 
                                          sx={{ 
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            color: desc ? 'text.primary' : 'text.secondary',
                                            fontStyle: desc ? 'normal' : 'italic',
                                          }}
                                        >
                                          {desc || '(No text assigned)'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          )}

          {/* ========================================== */}
          {/* TAB 2: Brand Library                       */}
          {/* ========================================== */}
          {tabValue === 2 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Master Promotions Library
                </Typography>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<Add />}
                  onClick={openLibraryAdd}
                  sx={{ borderRadius: '8px', color: '#fff' }}
                >
                  Create Master Promo
                </Button>
              </Box>

               {filteredLibraryPromotions.length === 0 ? (
                <Box textAlign="center" py={8} sx={{ bgcolor: 'rgba(0,0,0,0.01)', borderRadius: '12px', border: '2px dashed rgba(0,0,0,0.1)' }}>
                  <Label sx={{ fontSize: '48px', color: 'text.secondary', opacity: 0.5, mb: 2 }} />
                  <Typography variant="subtitle1" color="text.secondary">Promotion library is empty</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '400px', mx: 'auto', mt: 1 }}>
                    Add master promotions or load operator scans to discover historically used texts.
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {filteredLibraryPromotions.map((promo) => (
                    <Grid item xs={12} key={promo.id}>
                      <Card 
                        sx={{ 
                          borderRadius: '10px', 
                          border: promo.isMaster ? '2px solid rgba(255, 152, 0, 0.4)' : '1px dashed rgba(33, 150, 243, 0.5)',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                          }
                        }}
                      >
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                          <Box sx={{ flex: 1, minWidth: '250px' }}>
                            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {promo.DescriptiveText}
                              </Typography>
                              <Chip
                                label={promo.isMaster ? "Master Library" : `Discovered in Scans (${promo.captureCount}x)`}
                                size="small"
                                color={promo.isMaster ? "warning" : "info"}
                                variant={promo.isMaster ? "filled" : "outlined"}
                                sx={{ height: '20px', fontSize: '0.7rem', fontWeight: 'bold' }}
                              />
                            </Box>
                            {promo.Type && (() => {
                              const colors = getTypeColors(promo.Type);
                              return (
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      display: 'inline-block',
                                      bgcolor: colors.bg,
                                      color: colors.text,
                                      px: 1,
                                      py: 0.2,
                                      borderRadius: '4px',
                                      fontSize: '0.72rem',
                                      fontWeight: 600
                                    }}
                                  >
                                    🏷️ {promo.Type}
                                  </Typography>
                                </Box>
                              );
                            })()}
                            
                            {promo.AssociatedGames && promo.AssociatedGames.length > 0 && (
                              <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
                                {promo.AssociatedGames.map((game, i) => (
                                  <Chip key={i} label={getGameName(game)} size="small" variant="outlined" sx={{ height: '22px' }} />
                                ))}
                              </Box>
                            )}
                          </Box>

                          <Box display="flex" gap={1}>
                            {promo.isMaster ? (
                              <>
                                <IconButton 
                                  onClick={() => openLibraryEdit(promo)} 
                                  color="primary" 
                                  size="small"
                                  sx={{ bgcolor: 'rgba(33, 150, 243, 0.05)', '&:hover': { bgcolor: 'rgba(33, 150, 243, 0.1)' } }}
                                >
                                  <Edit size={18} />
                                </IconButton>
                                <IconButton 
                                  onClick={() => handleDeleteLibraryPromo(promo.id)} 
                                  color="error"
                                  size="small"
                                  sx={{ bgcolor: 'rgba(244, 67, 54, 0.05)', '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.1)' } }}
                                >
                                  <Delete size={18} />
                                </IconButton>
                              </>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                color="info"
                                startIcon={<Add />}
                                onClick={() => handlePinToLibrary(promo)}
                                sx={{ borderRadius: '6px', fontWeight: 'bold' }}
                              >
                                Pin to Library
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* ========================================== */}
          {/* Floating Bulk Action Bar (Glassmorphic)    */}
          {/* ========================================== */}
          {selectedBoxesList.length > 0 && (
            <Box 
              sx={{
                position: 'fixed',
                bottom: '30px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                width: '90%',
                maxWidth: '650px',
                p: 2,
                borderRadius: '16px',
                background: 'rgba(30, 30, 30, 0.85)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 152, 0, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                color: '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                animation: 'slideUp 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                '@keyframes slideUp': {
                  from: { bottom: '-100px', opacity: 0 },
                  to: { bottom: '30px', opacity: 1 },
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box 
                  sx={{ 
                    bgcolor: '#ff9800', 
                    borderRadius: '50%', 
                    width: '32px', 
                    height: '32px', 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    fontWeight: 'bold',
                    boxShadow: '0 0 10px rgba(255, 152, 0, 0.5)'
                  }}
                >
                  {selectedBoxesList.length}
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Promo Tiles Selected
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#b0bec5', display: 'block' }}>
                    Assign these tiles to a Master Promo.
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" gap={1.5}>
                <Button 
                  variant="text" 
                  onClick={clearSelection} 
                  sx={{ color: '#ffcc80', fontWeight: 600 }}
                >
                  Clear
                </Button>
                <Button 
                  variant="contained" 
                  color="warning" 
                  startIcon={<CheckCircle />}
                  onClick={openAlignWizard}
                  sx={{ borderRadius: '8px', fontWeight: 'bold', color: '#fff' }}
                >
                  Assign Promo
                </Button>
              </Box>
            </Box>
          )}

          {/* ========================================== */}
          {/* DIALOG 1: Bulk Alignment Wizard            */}
          {/* ========================================== */}
          <Dialog 
            open={alignDialogOpen} 
            onClose={() => !isAligning && setAlignDialogOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: { borderRadius: '16px', p: 1 }
            }}
          >
            <DialogTitle sx={{ fontWeight: 800 }}>
              Batch Promo Assignment
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                You are assigning <strong>{selectedBoxesList.length} promotion tiles</strong> to a Master Promo.
              </Typography>

              <Tabs
                value={assignmentMode}
                onChange={(e, val) => setAssignmentMode(val)}
                sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Assign to Existing Master Promo" value="existing" disabled={knownPromotions.length === 0} />
                <Tab label="Create New Master Promo" value="new" />
              </Tabs>

              {/* Tile Preview */}
              {selectedBoxesList.length > 0 && (
                <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>Preview of Selected Tile</Typography>
                  <Box sx={{ width: '100%', maxWidth: 400 }}>
                    <PromoTileCrop 
                      box={selectedBoxesList[0]}
                      imageLocation={selectedBoxesList[0].imageLocation || selectedBoxesList[0].s3Url || selectedBoxesList[0].image_url}
                      isSelected={false}
                      onToggleSelect={() => {}}
                    />
                  </Box>
                </Box>
              )}

              {/* MODE 1: Assign to Existing */}
              {assignmentMode === 'existing' && (
                <Box>
                  <Box sx={{ mb: 3 }}>
                    <Autocomplete
                      options={knownPromotions}
                      getOptionLabel={(option) => option.DescriptiveText || ''}
                      value={selectedMasterPromo}
                      onChange={(event, newValue) => {
                        setSelectedMasterPromo(newValue);
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Master Promo"
                          placeholder="Search and select from library..."
                          variant="outlined"
                          required
                        />
                      )}
                    />
                  </Box>

                  {/* Reference Text box for Original and Translated text */}
                  <Box sx={{ mb: 3, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" color="text.primary" gutterBottom>
                      Reference Text
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                      <strong>Original:</strong> {alignPromoTextOriginal?.toLowerCase() || 'None'}
                    </Typography>
                    {isTranslatingAlign && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                        Translating...
                      </Typography>
                    )}
                    {alignPromoTextTranslated && alignPromoTextTranslated !== alignPromoTextOriginal && (
                      <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                        <strong>Translated:</strong> {alignPromoTextTranslated.toLowerCase()}
                      </Typography>
                    )}
                  </Box>

                  {selectedMasterPromo && (
                    <Card variant="outlined" sx={{ mb: 3, p: 2, bgcolor: 'rgba(255, 152, 0, 0.02)', borderColor: 'rgba(255, 152, 0, 0.2)' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Master Promo Details
                      </Typography>
                      <Box display="flex" gap={2} sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Category:</strong> {selectedMasterPromo.Type || 'Promotion'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Games:</strong> {selectedMasterPromo.AssociatedGames?.length || 0} assigned
                        </Typography>
                      </Box>
                      {selectedMasterPromo.AssociatedGames?.length > 0 && (
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {selectedMasterPromo.AssociatedGames.map(gId => {
                            const gameObj = games.find(g => g.GameID === gId);
                            return (
                              <Chip key={gId} label={gameObj ? gameObj.Name : gId} size="small" variant="outlined" />
                            );
                          })}
                        </Box>
                      )}
                    </Card>
                  )}
                </Box>
              )}

              {/* MODE 2: Create New */}
              {assignmentMode === 'new' && (
                <Box>
                  {/* Master Text Editor */}
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      label="Master Promo Name"
                      variant="outlined"
                      value={alignPromoText}
                      onChange={(e) => setAlignPromoText(e.target.value)}
                      placeholder="e.g. Deposit £10, Get 50 Free Spins on Starburst"
                      multiline
                      rows={2}
                      required
                    />
                    {isTranslatingAlign && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5, pl: 1 }}>
                        Translating...
                      </Typography>
                    )}                    {assignmentMode === 'new' && (
                      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" color="text.primary" gutterBottom>
                          Reference Text
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, wordBreak: 'break-word' }}>
                          <strong>Original:</strong> {alignPromoTextOriginal?.toLowerCase() || 'None'}
                        </Typography>
                        {alignPromoTextTranslated && alignPromoTextTranslated !== alignPromoTextOriginal && (
                          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                            <strong>Translated:</strong> {alignPromoTextTranslated.toLowerCase()}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* 🏷️ Promotion Category Tag */}
                  <Box sx={{ mb: 3 }}>
                    <Autocomplete
                      freeSolo
                      options={promotionTypes.map(t => t.PromotionType)}
                      value={alignPromoType}
                      onChange={(event, newValue) => {
                        setAlignPromoType(newValue || 'Promotion');
                      }}
                      onInputChange={(event, newInputValue) => {
                        setAlignPromoType(newInputValue || 'Promotion');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Promotion Category / Tag"
                          placeholder="e.g. Signup Offer, Tournament, Free Spins"
                          variant="outlined"
                        />
                      )}
                    />
                  </Box>

                  {/* Associated Games */}
                  <Box sx={{ mb: 3 }}>
                    <Autocomplete
                      multiple
                      fullWidth
                      options={games}
                      filterSelectedOptions
                      filterOptions={(options, state) => {
                        const inputValue = (state.inputValue || '').toLowerCase().trim();
                        const unselectedOptions = options.filter(option => 
                          !alignPromoGames.includes(option.GameID)
                        );
                        if (!inputValue) {
                          return unselectedOptions.slice(0, 100);
                        }
                        const filtered = unselectedOptions.filter(option => 
                          (option.Name || '').toLowerCase().includes(inputValue)
                        );
                        filtered.sort((a, b) => {
                          const aName = (a.Name || '').toLowerCase();
                          const bName = (b.Name || '').toLowerCase();
                          const aStarts = aName.startsWith(inputValue);
                          const bStarts = bName.startsWith(inputValue);
                          if (aStarts && !bStarts) return -1;
                          if (!aStarts && bStarts) return 1;
                          return aName.localeCompare(bName);
                        });
                        return filtered.slice(0, 100);
                      }}
                      getOptionLabel={(option) => option.Name || ''}
                      value={alignPromoGamesObjects}
                      onChange={(event, newValue) => setAlignPromoGames(newValue.map(g => g.GameID))}
                      isOptionEqualToValue={(option, value) => option.GameID === value.GameID}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Associated Games (Optional)"
                          placeholder="Select games featured in this promotion"
                        />
                      )}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Button 
                        size="small" 
                        variant="text" 
                        color="primary" 
                        onClick={() => {
                          setCreateGameTarget('batch');
                          setIsCreateGameModalOpen(true);
                        }}
                      >
                        + Game not listed? Create New Game
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Selected Crops Carousel / Preview */}
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Visual Preview ({selectedBoxesList.length} Tiles to Update)
              </Typography>
              <Grid container spacing={1.5}>
                {selectedBoxesList.map((box) => (
                  <Grid item xs={4} sm={2.4} key={box.uniqueKey}>
                    <Box sx={{ position: 'relative' }}>
                      <PromoTileCrop
                        box={box}
                        imageLocation={box.imageLocation}
                        isSelected={false}
                        onToggleSelect={() => {}}
                      />
                      <Chip
                        label={box.dateLabel}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          bgcolor: 'rgba(0,0,0,0.85)',
                          color: '#fff',
                          fontSize: '0.65rem',
                          height: '16px',
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </DialogContent>
            
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={() => setAlignDialogOpen(false)} disabled={isAligning}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={executeBulkAlignment}
                disabled={isAligning || (assignmentMode === 'new' ? !alignPromoText.trim() : !selectedMasterPromo)}
                startIcon={isAligning ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
                sx={{ borderRadius: '8px', color: '#fff', fontWeight: 'bold' }}
              >
                {isAligning ? 'Assigning...' : 'Confirm Assignment'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* ========================================== */}
          {/* DIALOG 2: Create / Edit Library Promo       */}
          {/* ========================================== */}
          <Dialog 
            open={libraryDialogOpen} 
            onClose={() => setLibraryDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: { borderRadius: '12px' }
            }}
          >
            <DialogTitle sx={{ fontWeight: 800 }}>
              {editingPromotion ? 'Edit Master Promotion' : 'Add Master Promotion'}
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Master Descriptive Text"
                  variant="outlined"
                  value={newPromoText}
                  onChange={(e) => setNewPromoText(e.target.value)}
                  placeholder="e.g. Deposit £10, Get 50 Free Spins"
                  multiline
                  rows={2}
                  required
                />
              </Box>

              {/* 🏷️ Promotion Category Tag */}
              <Box sx={{ mb: 3 }}>
                <Autocomplete
                  freeSolo
                  options={promotionTypes.map(t => t.PromotionType)}
                  value={newPromoType}
                  onChange={(event, newValue) => {
                    setNewPromoType(newValue || 'Promotion');
                  }}
                  onInputChange={(event, newInputValue) => {
                    setNewPromoType(newInputValue || 'Promotion');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Promotion Category / Tag"
                      placeholder="e.g. Signup Offer, Tournament, Free Spins"
                      variant="outlined"
                    />
                  )}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Autocomplete
                  multiple
                  fullWidth
                  options={games}
                  filterSelectedOptions
                  filterOptions={(options, state) => {
                    const inputValue = (state.inputValue || '').toLowerCase().trim();
                    const unselectedOptions = options.filter(option => 
                      !newPromoGames.includes(option.GameID)
                    );
                    if (!inputValue) {
                      return unselectedOptions.slice(0, 100);
                    }
                    const filtered = unselectedOptions.filter(option => 
                      (option.Name || '').toLowerCase().includes(inputValue)
                    );
                    filtered.sort((a, b) => {
                      const aName = (a.Name || '').toLowerCase();
                      const bName = (b.Name || '').toLowerCase();
                      const aStarts = aName.startsWith(inputValue);
                      const bStarts = bName.startsWith(inputValue);
                      if (aStarts && !bStarts) return -1;
                      if (!aStarts && bStarts) return 1;
                      return aName.localeCompare(bName);
                    });
                    return filtered.slice(0, 100);
                  }}
                  getOptionLabel={(option) => option.Name || ''}
                  value={newPromoGamesObjects}
                  onChange={(event, newValue) => setNewPromoGames(newValue.map(g => g.GameID))}
                  isOptionEqualToValue={(option, value) => option.GameID === value.GameID}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Associated Games (Optional)"
                      placeholder="Select games featured in this promotion"
                    />
                  )}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button 
                    size="small" 
                    variant="text" 
                    color="primary" 
                    onClick={() => {
                      setCreateGameTarget('library');
                      setIsCreateGameModalOpen(true);
                    }}
                  >
                    + Game not listed? Create New Game
                  </Button>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
              <Button onClick={() => setLibraryDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={handleSaveLibraryPromo}
                disabled={!newPromoText.trim()}
                sx={{ borderRadius: '8px', color: '#fff' }}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Create Game Modal */}
      <CreateGameModal
        open={isCreateGameModalOpen}
        onClose={() => setIsCreateGameModalOpen(false)}
        initialGameName={createGameInitialName}
        onGameCreated={(newGame) => {
          if (createGameTarget === 'batch') {
            setAlignPromoGames(prev => [...prev, newGame.GameID]);
          } else {
            setNewPromoGames(prev => [...prev, newGame.GameID]);
          }
        }}
      />

      {/* Snackbar notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
          sx={{ width: '100%', borderRadius: '8px', fontWeight: 600 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* 🏷️ Reassign Promo Type Dropdown Menu */}
      <Menu
        anchorEl={typeMenuAnchorEl}
        open={Boolean(typeMenuAnchorEl)}
        onClose={handleCloseTypeMenu}
        PaperProps={{
          sx: {
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            minWidth: 220,
            maxHeight: 380,
            mt: 0.5,
          }
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Reassign Promo Type
          </Typography>
        </Box>
        {availablePromotionTypes.map((type) => {
          const isSelected = (selectedClusterForType?.promoType || 'Promotion').toLowerCase() === type.toLowerCase();
          const typeColor = getTypeColors(type);
          return (
            <MenuItem
              key={type}
              selected={isSelected}
              onClick={() => handleSelectType(type)}
              sx={{
                fontSize: '0.85rem',
                py: 1,
                px: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1.5,
                fontWeight: isSelected ? 700 : 500,
                '&.Mui-selected': {
                  bgcolor: `${typeColor.bg} !important`,
                },
                '&:hover': {
                  bgcolor: `${typeColor.bg}`,
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: typeColor.text,
                    flexShrink: 0
                  }}
                />
                <span style={{ color: typeColor.text }}>{type}</span>
              </Box>
              {isSelected && <CheckCircle sx={{ fontSize: 16, color: typeColor.text }} />}
            </MenuItem>
          );
        })}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={handleOpenCustomTypeDialog}
          sx={{
            fontSize: '0.85rem',
            py: 1,
            px: 2,
            color: '#ff9800',
            fontWeight: 600,
            '&:hover': {
              bgcolor: 'rgba(255, 152, 0, 0.08)'
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Add sx={{ fontSize: 18 }} />
            <span>+ Add new promo type...</span>
          </Box>
        </MenuItem>
      </Menu>

      {/* 🏷️ Add Custom Promo Type Dialog */}
      <Dialog
        open={addCustomTypeDialogOpen}
        onClose={() => {
          setAddCustomTypeDialogOpen(false);
          setSelectedClusterForType(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Add & Assign New Promo Type
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a new promotion type tag. This will be assigned to <strong>{selectedClusterForType?.originalText}</strong> and registered in your global promo types.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Promotion Type Name"
            placeholder="e.g. Free Spins, Tournament, Cash Drop"
            value={customTypeName}
            onChange={(e) => setCustomTypeName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && customTypeName.trim()) {
                e.preventDefault();
                handleConfirmCustomType();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button 
            onClick={() => {
              setAddCustomTypeDialogOpen(false);
              setSelectedClusterForType(null);
            }} 
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!customTypeName.trim()}
            onClick={handleConfirmCustomType}
            sx={{ color: '#fff', borderRadius: '8px', px: 2 }}
          >
            Apply & Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🧭 How to Use Guide Dialog (What's New style) */}
      <Dialog 
        open={whatsNewOpen} 
        onClose={handleCloseWhatsNew}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 24,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>✨</span> {whatsNewLang === 'en' ? "What's New" : "Novidades"}
          </Typography>
          <ToggleButtonGroup
            value={whatsNewLang}
            exclusive
            onChange={(e, val) => val && setWhatsNewLang(val)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                py: 0.2,
                px: 1.5,
                fontSize: '1.2rem',
                borderRadius: '8px',
              }
            }}
          >
            <ToggleButton value="en" title="English">🇬🇧</ToggleButton>
            <ToggleButton value="pt" title="Português (Brasil)">🇧🇷</ToggleButton>
          </ToggleButtonGroup>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            {whatsNewLang === 'en' 
              ? "This dashboard allows you to audit promotional screenshot tiles, correct typos, and link them to master promotions and games."
              : "Este painel permite auditar cards promocionais de capturas de tela, corrigir erros e vinculá-los a promoções master e jogos."
            }
          </Typography>
          
          <Stack spacing={2.5}>
            {(whatsNewLang === 'en' ? [
              {
                icon: "🔍",
                color: "rgba(25, 118, 210, 0.1)",
                title: "Filter & Select Website",
                text: "Use the filter options (Region, Country, Tag) at the top to narrow down operator sites, then choose a website to display its scanned promotional boxes."
              },
              {
                icon: "📊",
                color: "rgba(156, 39, 176, 0.1)",
                title: "Grouped Similarity & Timeline",
                text: "View promo boxes clustered by text similarity, or browse them chronologically. You can select multiple cards to batch consolidate their texts."
              },
              {
                icon: "✍️",
                color: "rgba(76, 175, 80, 0.1)",
                title: "Align Texts & Master Library",
                text: "Click 'Align Text' to associate a standard sentence from the operator's Master Library. Managing the library ensures perfect matches and resolves typos."
              },
              {
                icon: "📅",
                color: "rgba(255, 152, 0, 0.1)",
                title: "Recency Sync",
                text: "Assigning promotions automatically updates their 'Last Seen' date based on the newest screenshot capture date, keeping historical catalog data accurate."
              }
            ] : [
              {
                icon: "🔍",
                color: "rgba(25, 118, 210, 0.1)",
                title: "Filtrar e Selecionar Website",
                text: "Use as opções de filtro (Região, País, Tag) no topo para filtrar os sites dos operadores e escolha um site para carregar seus cards promocionais escaneados."
              },
              {
                icon: "📊",
                color: "rgba(156, 39, 176, 0.1)",
                title: "Semelhança e Linha do Tempo",
                text: "Veja os cards agrupados por semelhança de texto ou navegue de forma cronológica. Você pode selecionar múltiplos cards para consolidar os textos em lote."
              },
              {
                icon: "✍️",
                color: "rgba(76, 175, 80, 0.1)",
                title: "Alinhar Textos e Biblioteca Master",
                text: "Clique em 'Alinhar Texto' para associar uma frase padrão da Biblioteca Master. Gerenciar a biblioteca evita erros de digitação e inconsistências."
              },
              {
                icon: "📅",
                color: "rgba(255, 152, 0, 0.1)",
                title: "Sincronização de Data",
                text: "A atribuição de promoções atualiza automaticamente a data do 'Último Visto' com base na data da captura mais recente, mantendo o histórico de catálogo exato."
              }
            ]).map((step, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ fontSize: '1.75rem', bgcolor: step.color, p: 1, borderRadius: 2, lineHeight: 1 }}>
                  {step.icon}
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {step.text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button 
            onClick={handleDontShowWhatsNew} 
            sx={{ textTransform: 'none', color: '#666' }}
          >
            {whatsNewLang === 'en' ? "Don't Show Again" : "Não mostrar novamente"}
          </Button>
          <Button 
            variant="contained" 
            color="warning"
            onClick={handleCloseWhatsNew}
            sx={{ px: 3, borderRadius: 2, color: '#fff' }}
          >
            {whatsNewLang === 'en' ? "Got it!" : "Entendi!"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromoManagement;
